import React, { useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Infinity as InfinityIcon } from 'lucide-react';

/**
 * <DurationPicker> — Plan membership duration with friendlier admin UX.
 *
 * Persists as `duration_days` for backend compatibility:
 *   - Unlimited        → 0
 *   - Custom (preset)  → preset_days (30 / 90 / 180 / 365 / 730 / 1095 / 1825 / 2192 / 3650)
 *   - Years input      → years × 365
 *
 * Props:
 *   days     — current duration_days value (number)
 *   onChange — (newDays:number) => void
 */
const PRESETS = [
  { label: '1 month', days: 30 },
  { label: '3 months', days: 90 },
  { label: '6 months', days: 180 },
  { label: '1 year', days: 365 },
  { label: '2 years', days: 730 },
  { label: '3 years', days: 1095 },
  { label: '5 years', days: 1825 },
  { label: '6 years', days: 2192 },
  { label: '10 years', days: 3650 },
];

export function DurationPicker({ days = 365, onChange }) {
  const isUnlimited = Number(days) === 0;

  // What kind of value is currently selected?
  const matchedPreset = useMemo(() => PRESETS.find((p) => p.days === Number(days)), [days]);
  const initialMode = isUnlimited ? 'unlimited' : (matchedPreset ? 'preset' : 'years');
  const [mode, setMode] = React.useState(initialMode);
  const [years, setYears] = React.useState(() => {
    const y = Math.max(1, Math.round(Number(days) / 365)) || 1;
    return y;
  });

  // Sync when external `days` prop changes (e.g. when opening a different plan)
  React.useEffect(() => {
    const next = Number(days);
    if (next === 0) {
      setMode('unlimited');
    } else if (PRESETS.some((p) => p.days === next)) {
      setMode('preset');
    } else {
      setMode('years');
      setYears(Math.max(1, Math.round(next / 365)));
    }
  }, [days]);

  const toUnlimited = (on) => {
    if (on) {
      setMode('unlimited');
      onChange(0);
    } else {
      setMode('preset');
      onChange(365);
    }
  };

  return (
    <div className="space-y-3" data-testid="duration-picker">
      {/* Unlimited toggle */}
      <div className="flex items-center justify-between p-2 rounded bg-[#000B18] border border-white/10">
        <Label className="text-[#A0B4CC] text-sm flex items-center gap-2">
          <InfinityIcon className="w-4 h-4 text-[#00E5FF]" />
          Unlimited / Lifetime membership
        </Label>
        <Switch
          checked={isUnlimited}
          onCheckedChange={toUnlimited}
          data-testid="duration-unlimited-switch"
        />
      </div>

      {!isUnlimited && (
        <>
          {/* Preset chips */}
          <div className="flex flex-wrap gap-1.5" data-testid="duration-presets">
            {PRESETS.map((p) => {
              const active = mode === 'preset' && Number(days) === p.days;
              return (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => { setMode('preset'); onChange(p.days); }}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors border ${
                    active
                      ? 'bg-[#00E5FF] text-[#000B18] border-[#00E5FF] font-medium'
                      : 'border-white/15 text-[#A0B4CC] hover:border-white/40 hover:text-white'
                  }`}
                  data-testid={`duration-preset-${p.days}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Custom years input */}
          <div className="flex items-center gap-2">
            <Label className="text-[#5C7599] text-xs whitespace-nowrap">Or custom:</Label>
            <Input
              type="number"
              min={1}
              max={99}
              value={years}
              onChange={(e) => {
                const y = Math.max(1, parseInt(e.target.value, 10) || 1);
                setYears(y);
                setMode('years');
                onChange(y * 365);
              }}
              className="bg-[#000B18] border-white/20 text-white w-20"
              data-testid="duration-years-input"
            />
            <span className="text-[#A0B4CC] text-sm">year{years === 1 ? '' : 's'}</span>
            <span className="text-[#5C7599] text-xs ml-auto">= {Number(days || 0).toLocaleString()} days</span>
          </div>
        </>
      )}

      {isUnlimited && (
        <p className="text-[#5C7599] text-xs italic px-1">
          Members on this plan will have lifetime access — subscriptions never expire.
        </p>
      )}
    </div>
  );
}

export default DurationPicker;
